// ============================================================================
// 📦 VERSION: 2026.01.15.1200
// ============================================================================
// ADVERT CONFIG - Fetches fallback ads from database API
// Maintains backward compatibility with existing code
// ============================================================================
// CHANGELOG:
// 2026.01.15.1200 - Fixed missing getAdConfig function (was not exported)
// 2026.01.13.1700 - Initial version
// ============================================================================

const API_URL = 'https://api.traktag.com/api';

// Default placeholders
const defaultConfig = {
  mobileBanner: {
    url: 'https://via.placeholder.com/320x50/4A5568/FFFFFF?text=Advertise+Here',
    html: '',
    clickUrl: 'https://traktag.com/advertise',
    width: 320,
    height: 50
  },
  desktopBanner: {
    url: 'https://via.placeholder.com/728x90/4A5568/FFFFFF?text=Advertise+Here',
    html: '',
    clickUrl: 'https://traktag.com/advertise',
    width: 728,
    height: 90
  },
  rectangle: {
    url: 'https://via.placeholder.com/300x250/4A5568/FFFFFF?text=Advertise+Here',
    html: '',
    clickUrl: 'https://traktag.com/advertise',
    width: 300,
    height: 250
  },
  skyscraper: {
    url: 'https://via.placeholder.com/300x600/4A5568/FFFFFF?text=Advertise+Here',
    html: '',
    clickUrl: 'https://traktag.com/advertise',
    width: 300,
    height: 600
  },
  interstitial: {
    url: 'https://via.placeholder.com/320x480/4A5568/FFFFFF?text=Advertise+Here',
    html: '',
    clickUrl: 'https://traktag.com/advertise',
    width: 320,
    height: 480
  },
  square: {
    url: 'https://via.placeholder.com/250x250/4A5568/FFFFFF?text=Advertise+Here',
    html: '',
    clickUrl: 'https://traktag.com/advertise',
    width: 250,
    height: 250
  }
};

// Create the config object
const advertConfig = { ...defaultConfig };

// Track if settings have been loaded
let settingsLoaded = false;
let settingsPromise = null;

// Fetch and update config from API
const fetchGlobalSettings = async () => {
  if (settingsPromise) return settingsPromise;
  
  settingsPromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/settings`);
      if (!response.ok) return;
      
      const settings = await response.json();
      
      // Update mobile banner
      if (settings.mobile_html) {
        advertConfig.mobileBanner.html = settings.mobile_html;
      }
      if (settings.mobile_image) {
        advertConfig.mobileBanner.url = settings.mobile_image;
      }
      if (settings.mobile_click_url || settings.fallback_click_url) {
        advertConfig.mobileBanner.clickUrl = settings.mobile_click_url || settings.fallback_click_url;
      }
      
      // Update desktop banner
      if (settings.desktop_html) {
        advertConfig.desktopBanner.html = settings.desktop_html;
      }
      if (settings.desktop_image) {
        advertConfig.desktopBanner.url = settings.desktop_image;
      }
      if (settings.desktop_click_url || settings.fallback_click_url) {
        advertConfig.desktopBanner.clickUrl = settings.desktop_click_url || settings.fallback_click_url;
      }
      
      // Update rectangle
      if (settings.rectangle_html) {
        advertConfig.rectangle.html = settings.rectangle_html;
      }
      if (settings.rectangle_image) {
        advertConfig.rectangle.url = settings.rectangle_image;
      }
      if (settings.rectangle_click_url || settings.fallback_click_url) {
        advertConfig.rectangle.clickUrl = settings.rectangle_click_url || settings.fallback_click_url;
      }
      
      // Update skyscraper
      if (settings.skyscraper_html) {
        advertConfig.skyscraper.html = settings.skyscraper_html;
      }
      if (settings.skyscraper_image) {
        advertConfig.skyscraper.url = settings.skyscraper_image;
      }
      if (settings.skyscraper_click_url || settings.fallback_click_url) {
        advertConfig.skyscraper.clickUrl = settings.skyscraper_click_url || settings.fallback_click_url;
      }
      
      // Update interstitial
      if (settings.interstitial_html) {
        advertConfig.interstitial.html = settings.interstitial_html;
      }
      if (settings.interstitial_image) {
        advertConfig.interstitial.url = settings.interstitial_image;
      }
      if (settings.interstitial_click_url || settings.fallback_click_url) {
        advertConfig.interstitial.clickUrl = settings.interstitial_click_url || settings.fallback_click_url;
      }
      
      settingsLoaded = true;
      console.log('✅ advertConfig: Global settings loaded from API');
    } catch (error) {
      console.error('advertConfig: Failed to fetch settings:', error);
    }
  })();
  
  return settingsPromise;
};

// Fetch settings on load (non-blocking)
fetchGlobalSettings();

/**
 * Get ad configuration for a specific ad type
 * @param {string} type - 'mobile', 'desktop', 'rectangle', 'skyscraper', 'interstitial'
 * @param {object} quizAd - Optional quiz-specific ad override { url, html, clickUrl }
 * @returns {object} - { type: 'html'|'image'|'placeholder', content, clickUrl, width, height }
 */
export const getAdConfig = async (type, quizAd = null) => {
  // Wait for settings to load (with timeout)
  if (!settingsLoaded) {
    await Promise.race([
      fetchGlobalSettings(),
      new Promise(resolve => setTimeout(resolve, 2000)) // 2 second timeout
    ]);
  }
  
  // Map type to config key
  const typeMap = {
    'mobile': 'mobileBanner',
    'desktop': 'desktopBanner',
    'rectangle': 'rectangle',
    'skyscraper': 'skyscraper',
    'interstitial': 'interstitial',
    'square': 'square'
  };
  
  const configKey = typeMap[type] || 'mobileBanner';
  const globalAd = advertConfig[configKey];
  
  // Priority: Quiz-specific HTML > Quiz-specific Image > Global HTML > Global Image > Placeholder
  
  // Check quiz-specific ad first
  if (quizAd) {
    if (quizAd.html && quizAd.html.trim()) {
      return {
        type: 'html',
        content: quizAd.html,
        clickUrl: quizAd.clickUrl || globalAd.clickUrl,
        width: globalAd.width,
        height: globalAd.height
      };
    }
    if (quizAd.url && quizAd.url.trim() && !quizAd.url.includes('placeholder.com')) {
      return {
        type: 'image',
        content: quizAd.url,
        clickUrl: quizAd.clickUrl || globalAd.clickUrl,
        width: globalAd.width,
        height: globalAd.height
      };
    }
  }
  
  // Check global HTML (AdSense)
  if (globalAd.html && globalAd.html.trim()) {
    return {
      type: 'html',
      content: globalAd.html,
      clickUrl: globalAd.clickUrl,
      width: globalAd.width,
      height: globalAd.height
    };
  }
  
  // Check global image
  if (globalAd.url && globalAd.url.trim() && !globalAd.url.includes('placeholder.com')) {
    return {
      type: 'image',
      content: globalAd.url,
      clickUrl: globalAd.clickUrl,
      width: globalAd.width,
      height: globalAd.height
    };
  }
  
  // Return placeholder
  return {
    type: 'image',
    content: `https://via.placeholder.com/${globalAd.width}x${globalAd.height}/4A5568/FFFFFF?text=Advertise+Here`,
    clickUrl: globalAd.clickUrl,
    width: globalAd.width,
    height: globalAd.height
  };
};

export default advertConfig;
